import { useQuery } from '@tanstack/react-query'
import { checkResetCode } from '../api/users'

export type LinkLiveness = 'checking' | 'live' | 'dead'

/**
 * Whether a mailed password link can still be used, asked before the password
 * fields are shown.
 *
 * The pages a link opens used to render the fields straight away and only find
 * out at submit, so a dead link cost you a composed password before it told you
 * anything. Asking first is cheap: the check spends nothing, so the link is
 * exactly as usable afterwards.
 *
 * Only an explicit "no" counts as dead. A failed check -- offline, timeout, the
 * API down -- reports 'live' and lets submit settle it, because the two
 * mistakes do not cost the same: a wrong 'dead' sends somebody holding a
 * working link off to request a new one, and requesting one retires the link
 * they had. A wrong 'live' only costs them the password they typed, which is
 * where we started.
 */
export const useLinkLiveness = (token?: string): LinkLiveness => {
  const hasToken = token != null && token.length > 0

  const { data, isPending, isError } = useQuery({
    queryKey: [{ scope: 'auth', entity: 'reset-code', token }],
    queryFn: async () => {
      const response = await checkResetCode(token as string)
      return response.data.data
    },
    enabled: hasToken,
    // A link does not come back to life, and asking again on every window
    // focus would only spend requests to hear the same answer.
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  })

  if (!hasToken) {
    return 'dead'
  }

  if (isPending) {
    return 'checking'
  }

  return isError || data !== false ? 'live' : 'dead'
}
